import { useQuery } from "@apollo/client";
import { GET_USER } from "graphql/queries";
import { JSX, useEffect, useMemo } from "react";
import { GetUserQuery, PermissionEnum } from "types";

export type UserPermissions = {
  isAdminOfSingleCompany: string | undefined | null;
  isAuremAdmin: boolean;
  isAuremOrPlatformAdmin: boolean;
  isCompanyAdmin: boolean;
  isCompanyAdminOf: (companyId: string | undefined | null) => boolean;
  isMohre: boolean;
  isPlatformAdmin: boolean;
  isSupport: boolean;
  isTrusteeAdmin: boolean;
};

export const useUserData = () => {
  const { data, loading } = useQuery(GET_USER);
  return {
    user: data?.user,
    loading,
  };
};

export const useHasPlatformRole = (): boolean => {
  const { user } = useUserData();
  return useMemo(
    () =>
      user?.userRoles?.some((userRole) => userRole.role.isPlatform) ?? false,
    [user],
  );
};

export const isCompanyAdminUser = (
  user: Pick<GetUserQuery["user"], "userRoles"> | undefined | null,
): boolean =>
  user?.userRoles.some((userRole) => userRole.company !== null) ?? false;

export const useUserPermissions = () => {
  const { user } = useUserData();
  const userRoles = user?.userRoles ?? [];
  const permissions = new Set<PermissionEnum>();
  for (const userRole of userRoles) {
    const role = userRole.role;
    const rolePermissions = role.rolePermissions;
    if (!rolePermissions) continue;
    for (const rolePermission of rolePermissions) {
      permissions.add(rolePermission.permission);
    }
  }

  if (userRoles.length === 0) {
    permissions.add(PermissionEnum.CreateCompany);
  }

  function permissionsContain(
    permissionsSet: Set<PermissionEnum>,
    permissions: PermissionEnum[],
  ): boolean {
    return permissions.some((permission) => permissionsSet.has(permission));
  }

  return {
    userPermissions: permissions,
    userRoles,
    permissionsContain,
  };
};

export const useCompaniesWithPermission = (
  companyIds: string[],
  permission: PermissionEnum,
) => {
  const { user } = useUserData();
  const userRoles: GetUserQuery["user"]["userRoles"] = user?.userRoles ?? [];
  const permissions: Record<string, boolean> = {};
  for (const userRole of userRoles) {
    const role = userRole.role;
    const rolePermissions = role.rolePermissions;
    const company = userRole.company;
    if (!rolePermissions) continue;
    if (
      rolePermissions.some(
        (rolePermission) => rolePermission.permission === permission,
      )
    ) {
      if (role.isPlatform) {
        permissions["all"] = true;
      } else if (companyIds.includes(company?.id ?? "")) {
        permissions[company?.id ?? ""] = true;
      }
    }
  }
  return permissions;
};

export const authorize = (
  userRoles: GetUserQuery["user"]["userRoles"],
  permission: PermissionEnum,
  companyId?: string,
) => {
  if (userRoles.length === 0 && permission === PermissionEnum.CreateCompany) {
    return true;
  }
  for (const userRole of userRoles) {
    const role = userRole.role;
    const rolePermissions = role.rolePermissions;
    const company = userRole.company;
    if (!rolePermissions) continue;
    if (
      rolePermissions.some(
        (rolePermission) => rolePermission.permission === permission,
      )
    ) {
      if (
        role.isPlatform ||
        (companyId && company?.id === companyId) ||
        permission === PermissionEnum.CreateCompany
      ) {
        return true;
      }
    }
  }
  return false;
};

export function useAuthorize(
  permission: PermissionEnum,
  companyId?: string,
): boolean;
export function useAuthorize(
  permissions: PermissionEnum[],
  options?: {
    companyId?: string;
    mode?: "any" | "all";
  },
): boolean;
export function useAuthorize(
  permission: PermissionEnum | PermissionEnum[],
  optionsOrCompanyId?:
    | string
    | {
        companyId?: string;
        mode?: "any" | "all";
      },
): boolean {
  const { user } = useUserData();
  const userRoles: GetUserQuery["user"]["userRoles"] = user?.userRoles ?? [];

  return useMemo(() => {
    const permissions = Array.isArray(permission) ? permission : [permission];

    let companyId: string | undefined;
    let mode: "any" | "all" = "any";

    if (typeof optionsOrCompanyId === "string") {
      companyId = optionsOrCompanyId;
    } else if (optionsOrCompanyId) {
      companyId = optionsOrCompanyId.companyId;
      mode = optionsOrCompanyId.mode ?? "any";
    }

    const checkPermission = (p: PermissionEnum) => {
      return authorize(userRoles, p, companyId);
    };

    if (mode === "all") {
      return permissions.every(checkPermission);
    } else {
      return permissions.some(checkPermission);
    }
  }, [userRoles, permission, optionsOrCompanyId]);
}

export const useAuthorizeRender = () => {
  const { user } = useUserData();
  const userRoles: GetUserQuery["user"]["userRoles"] = user?.userRoles ?? [];
  const authorizeRender = (
    permission: PermissionEnum,
    render: JSX.Element,
    companyId?: string,
  ) => {
    return authorize(userRoles, permission, companyId) ? render : null;
  };

  return authorizeRender;
};

export const useInitUserQuery = (options?: { skip?: boolean }) => {
  const { data, error, loading } = useQuery(GET_USER, {
    errorPolicy: "none",
    skip: options?.skip,
  });
  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { data, loading };
};
